const has = (value, key) => value != null && Object.prototype.hasOwnProperty.call(value, key);
const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value) &&
  (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);

// Optional imported details never add defaults to legacy documents or dirty signatures.
export function cloneTrainingMetadata(value) {
  if (Array.isArray(value)) return value.map(cloneTrainingMetadata);
  if (isRecord(value)) return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, cloneTrainingMetadata(entry)]));
  return value;
}

export function optionalTrainingMetadata(source, fields) {
  return Object.fromEntries(fields.filter(field => has(source, field)).map(field => [field, cloneTrainingMetadata(source[field])]));
}

export function validateTrainingMetadata(plan) {
  const errors = [];
  const report = (label, message) => errors.push(`${label}: ${message}`);
  const shape = (value, fields, label) => {
    if (!isRecord(value)) { report(label, 'il valore deve essere un oggetto valido.'); return false; }
    const unknown = Object.keys(value).filter(key => !fields.includes(key));
    if (unknown.length) report(label, `campi non supportati (${unknown.join(', ')}).`);
    return true;
  };
  const text = (value, label, max, required = false) => {
    if (typeof value !== 'string') { report(label, 'il valore deve essere un testo.'); return false; }
    if (required && !value.trim()) report(label, 'inserisci un valore.');
    if (value.length > max) report(label, `massimo ${max} caratteri.`);
    return true;
  };
  const strings = (value, label, maxItems = 40) => {
    if (!Array.isArray(value) || value.length > maxItems) { report(label, `inserisci un elenco con massimo ${maxItems} valori.`); return; }
    value.forEach((entry, index) => text(entry, `${label} ${index + 1}`, 200));
  };
  const optionalTexts = (value, fields, label, max = 200) => {
    fields.forEach(field => { if (has(value, field)) text(value[field], `${label}, ${field}`, max); });
  };
  const levels = new Set();
  if (has(plan, 'details')) {
    const short = ['goalType', 'goal', 'format', 'whoCanUse', 'gender', 'createdBy', 'lastUpdate', 'sourceId', 'frequency', 'duration'];
    const long = ['description', 'nutritionalTips', 'lifestyleTips'];
    if (shape(plan.details, [...short, ...long, 'locations', 'aspirations', 'levels'], 'Dettagli programma')) {
      optionalTexts(plan.details, short, 'Dettagli programma');
      optionalTexts(plan.details, long, 'Dettagli programma', 20000);
      ['locations', 'aspirations'].forEach(field => { if (has(plan.details, field)) strings(plan.details[field], field); });
      if (has(plan.details, 'levels')) {
        if (!Array.isArray(plan.details.levels) || plan.details.levels.length > 100) {
          report('Livelli', 'inserisci un elenco con massimo 100 livelli.');
        } else plan.details.levels.forEach((level, index) => {
          const label = `Livello ${index + 1}`;
          if (!shape(level, ['id', 'name'], label)) return;
          if (typeof level.id !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(level.id)) report(label, 'identificativo non valido.');
          else if (levels.has(level.id)) report(label, 'identificativo duplicato.');
          else levels.add(level.id);
          text(level.name, `${label}, nome`, 160, true);
        });
      }
    }
  }
  if (has(plan, 'source') && shape(plan.source, ['provider', 'id', 'url', 'readAt', 'contentHash', 'visibility', 'library'], 'Provenienza')) {
    optionalTexts(plan.source, ['provider', 'id', 'readAt', 'contentHash', 'visibility', 'library'], 'Provenienza');
    if (has(plan.source, 'url') && text(plan.source.url, 'Provenienza, URL', 2000) && plan.source.url.trim()) {
      try {
        const url = new URL(plan.source.url);
        if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) throw new Error('Invalid source URL');
      } catch { report('Provenienza, URL', 'inserisci un indirizzo HTTPS valido senza credenziali.'); }
    }
  }
  for (const [workoutIndex, workout] of (Array.isArray(plan?.workouts) ? plan.workouts : []).entries()) {
    if (!isRecord(workout)) continue;
    const label = `Workout ${workoutIndex + 1}`;
    if (has(workout, 'levelId') && text(workout.levelId, `${label}, livello`, 128) && workout.levelId && !levels.has(workout.levelId)) {
      report(`${label}, livello`, 'il livello indicato non esiste nel programma.');
    }
    if (has(workout, 'summary') && shape(workout.summary, ['list', 'detail'], `${label}, riepilogo`)) {
      ['list', 'detail'].forEach(view => {
        if (has(workout.summary, view) && shape(workout.summary[view], ['minutes', 'kcal', 'moves'], `${label}, riepilogo ${view}`)) {
          optionalTexts(workout.summary[view], ['minutes', 'kcal', 'moves'], `${label}, riepilogo ${view}`, 80);
        }
      });
    }
    for (const [exerciseIndex, exercise] of (Array.isArray(workout.exercises) ? workout.exercises : []).entries()) {
      if (!isRecord(exercise)) continue;
      const exerciseLabel = `${label}, esercizio ${exerciseIndex + 1}`;
      if (has(exercise, 'details') && shape(exercise.details, ['muscles', 'target', 'sourceId', 'imageUrl'], `${exerciseLabel}, dettagli`)) {
        optionalTexts(exercise.details, ['target', 'sourceId'], `${exerciseLabel}, dettagli`);
        if (has(exercise.details, 'muscles')) strings(exercise.details.muscles, `${exerciseLabel}, muscoli`);
        if (has(exercise.details, 'imageUrl') && text(exercise.details.imageUrl, `${exerciseLabel}, immagine`, 2000) && exercise.details.imageUrl.trim()) {
          try {
            const url = new URL(exercise.details.imageUrl);
            if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) throw new Error('Invalid image URL');
          } catch { report(`${exerciseLabel}, immagine`, 'inserisci un indirizzo HTTPS valido senza credenziali.'); }
        }
      }
      for (const [setIndex, set] of (Array.isArray(exercise.sets) ? exercise.sets : []).entries()) {
        if (!isRecord(set) || !has(set, 'extraParameters')) continue;
        const setLabel = `${exerciseLabel}, serie ${setIndex + 1}, parametri aggiuntivi`;
        if (!Array.isArray(set.extraParameters) || set.extraParameters.length > 40) {
          report(setLabel, 'inserisci un elenco con massimo 40 parametri.');
          continue;
        }
        set.extraParameters.forEach((parameter, index) => {
          const parameterLabel = `${setLabel} ${index + 1}`;
          if (!shape(parameter, ['label', 'unit', 'value'], parameterLabel)) return;
          text(parameter.label, `${parameterLabel}, etichetta`, 120, true);
          text(parameter.unit, `${parameterLabel}, unità`, 40);
          text(parameter.value, `${parameterLabel}, valore`, 500);
        });
      }
    }
  }
  return errors;
}
